import { useParams, useSearchParams } from "react-router-dom";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  DocumentReviewActions,
  DocumentReviewFallback,
  DocumentReviewPreview,
} from "@/accounts-payable/common";
import { useGetReceipt } from "@/accounts-payable/query";
import {
  AP_TAB,
  AP_TAB_LABEL,
  apListPath,
  parseApTab,
  stFilingTarget,
} from "@/accounts-payable/utils";
import { useBreadcrumbs } from "@/app-shell";
import { useBusiness, useBusinessInUrl } from "@/core/business";
import { ReceiptReviewHeader } from "./receipt-review-header";

/**
 * Full-page review of one receipt: the document beside the agent's proposals,
 * approved or rejected in place — the receipt counterpart of the invoice page.
 */
const AccountsPayableReceiptPage = () => {
  const { receiptId } = useParams<{ receiptId: string }>();
  const { businessId } = useBusiness();
  useBusinessInUrl();
  const [searchParams] = useSearchParams();
  const fromTab = parseApTab(searchParams.get("tab")) ?? AP_TAB.receipts;

  const {
    data: receipt,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useGetReceipt(businessId, receiptId);

  useBreadcrumbs([
    { label: "Accounts payable", href: apListPath() },
    {
      label: AP_TAB_LABEL[fromTab],
      href: apListPath(fromTab, searchParams.toString()),
    },
    {
      label: receipt?.receipt_number
        ? `Receipt ${receipt.receipt_number}`
        : "Receipt",
    },
  ]);

  if (!receipt) {
    return (
      <PageContainer>
        <DocumentReviewFallback
          documentType="receipt"
          loading={isLoading}
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ReceiptReviewHeader receipt={receipt} businessId={businessId} />

      <Stack
        align="start"
        gap={0}
        className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_480px]"
      >
        <DocumentReviewPreview document={receipt} />
        <DocumentReviewActions
          documentId={receipt.id}
          documentType="receipt"
          documentHasJob={stFilingTarget(receipt) != null}
          businessId={businessId}
        />
      </Stack>
    </PageContainer>
  );
};

export default AccountsPayableReceiptPage;
